import { useEffect, useRef, useState } from "react";
import CabAvatar from "@CabComponents/CabAvatar";
import { Box, styled, Typography, useMediaQuery, Alert } from "@mui/material";
import colors from "../../colors";
import { CabButton } from "@CabComponents/CabButton";
import { 
  BookingSlot, CreateUser, ExternalMeetingInfo, FetchReturn, MeetingStatus, PublicLeader 
} from "../../store";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { CabControlLabel } from "@CabComponents/CabControlLabel";
import { humanReadableDuration } from "../../utils/scheduleUtils";
import theme from "../../theme";
import CabSpinner from "@CabComponents/CabSpinner";
import { PublicOrganization } from "../../store/cabinetApi/generated/core";
import { successConfetti } from "../../utils/renderUtils";
import BookMeetingWrapper from "../BookMeeting/BookMeetingWrapper";
import { NAVY_LOGO_SRC } from "../../resourceUrls";
import { useElementBreakpoint } from "../../utils/hooks";

export enum MeetingCancelationStatus {
  INITIAL = 0,
  SUCCESS = 1,
  ERROR = 2,
  CANCELED = 3,
}

export type Props = {
  handleCancelMeeting: (message: string) => Promise<FetchReturn | undefined>;
  meeting: ExternalMeetingInfo | null;
  dateSet: BookingSlot | null;
  currentTimeZone: string;
  leaders: PublicLeader[];
  confirmed: boolean;
  organization?: PublicOrganization;
  name: string;
  loading: boolean;
};

const MeetingCancelation = ({
  leaders, confirmed, handleCancelMeeting, loading, ...meetingInfoProps
}: Props) => {
  const [canceling, setCanceling] = useState(false);
  const [cancelStatus, setCancelStatus] = useState(MeetingCancelationStatus.INITIAL);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    if (meetingInfoProps.meeting?.status.id === MeetingStatus.CANCELLED) {
      setCancelStatus(MeetingCancelationStatus.CANCELED);
    }
  }, [meetingInfoProps]);

  const cancelMeeting = async (message: string) => {
    setCanceling(true);
    const res = await handleCancelMeeting(message);
    setCanceling(false);
    if (res?.status === 200) {
      setCancelStatus(MeetingCancelationStatus.SUCCESS);
      successConfetti();
    } else {
      setCancelStatus(MeetingCancelationStatus.ERROR);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const stepBreakpoint = useElementBreakpoint(containerRef, {790: "xs", 791: "sm", 1100: "md", 1101: "lg"}, 'lg');
  const isSmDown = ["xs"].some(bp => bp === stepBreakpoint);

  const meetingTitle = meetingInfoProps.meeting?.status.id === MeetingStatus.SCHEDULED && 
    meetingInfoProps.meeting.title_booked ?
    meetingInfoProps.meeting.title_booked :
    meetingInfoProps.meeting?.title || '';

  const meetingDescription = meetingInfoProps.meeting?.status.id === MeetingStatus.SCHEDULED && 
    meetingInfoProps.meeting.description_booked ? 
    meetingInfoProps.meeting.description_booked : 
    meetingInfoProps.meeting?.description || '';

  const durationText = humanReadableDuration(meetingInfoProps.meeting?.duration_minutes || 0);

  const logo = meetingInfoProps.meeting?.create_user.active_license?.organization.logo || null;
  
  if (!meetingInfoProps.meeting) {
    if (loading) {
      return (
        <Box sx={{
          marginTop: 10
        }}>
          <CabSpinner />
        </Box>
      );
    }

    return (
      <Box>
        <Alert severity="warning" variant="outlined">
          Meeting not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        width: "100%",
        gap: isSmDown ? 1 : 4
      }}>
      {isSmDown && logo && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: 1
          }}>
          <Box component='img' src={logo} alt='' 
            sx={{ maxWidth: 250, maxHeight: 54, objectFit: 'contain' }}
          />
        </Box>
      )}
      <BookMeetingWrapper
        showSidePanel={isMdUp}
        logo={logo || undefined}
        loadingPage={false}
        meetingInfo={meetingInfoProps.meeting}
        meetingTitle={meetingTitle}
        meetingDescription={meetingDescription}
        durationText={durationText}
        slotSelected={meetingInfoProps.dateSet || undefined}
        isAuthenticated={true}
        templateVars={{}}
        header={<></>}
        showBackButton={false}
        showSubmitButton={false}
        step={0}
        hideFooter={true}
        sx={{
          borderRadius: 5,
          border: `1px solid ${colors.black200}`,
          backgroundColor: colors.white900,
          overflow: 'hidden',
          margin: { xs: 4, lg: 1 },
          maxWidth: 1040,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: 4
          }}>
          {cancelStatus === MeetingCancelationStatus.INITIAL && (
            <InitialCancelationView
              leaders={leaders}
              cancelMeeting={cancelMeeting}
              canceling={canceling}
            />
          )}
          {cancelStatus === MeetingCancelationStatus.SUCCESS && (
            <SuccessCancelationView />
          )}
          {cancelStatus === MeetingCancelationStatus.ERROR && (
            <ErrorCancelationView user={meetingInfoProps.meeting?.create_user} />
          )}
          {cancelStatus === MeetingCancelationStatus.CANCELED && (
            <CanceledCancelationView />
          )}
        </Box>
      </BookMeetingWrapper>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'center'
        }}>
        <Typography variant="caption" sx={{
          fontSize: 12
        }}>Powered by</Typography>
        <Box component='img' src={NAVY_LOGO_SRC} alt='Cabinet' sx={{
          width: 110
        }} />
      </Box>
    </Box>
  );
};

type CancelationViewProps = {
  leaders: PublicLeader[]
  cancelMeeting: (message: string) => void,
  canceling: boolean
};

const InitialCancelationView = ({leaders, cancelMeeting, canceling}: CancelationViewProps) => {
  const [message, setMessage] = useState("");
  
  return (
    <StyledBox>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: 500
        }}>
        <TitleTypography>
          Are you sure you want to cancel this meeting?
        </TitleTypography>
        <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
          {leaders.map((leader) => {
            const name = leader?.first_name + ' ' + leader?.last_name;
            return (
              <Box
                key={leader.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "0px 8px"
                }}>
                <CabAvatar
                  color={leader.color}
                  src={leader?.pic_url}
                  size="small"
                  name={`${leader.first_name} ${leader.last_name}`}
                />
                <Typography sx={{ paddingBottom: 1 }}>
                  {name}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box>
          <Typography sx={{ marginTop: 2, marginBottom: 2 }}>
            This event will be removed from the calendar and attendees will be notified.
          </Typography>
        </Box>
        <Box sx={{width: "100%"}}>
          <CabControlLabel
            labelPlacement="top"
            sx={{
              marginBottom: 3,
              width: "100%",
              textAlign: "left",
              alignItems: "start",
              "& .MuiFormControl-root": {
                width: "100%",
              },
              "& .MuiTypography-root": {
                fontWeight: "bold",
                marginBottom: 1
              }
            }}
            label="Send a message to the organizer (optional)"
            control={<CabTextInput
              multiline
              sx={{width: "100%"}}
              minRows={3}
              maxRows={6}
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
            />}
          />
        </Box>
        <CabButton onClick={() => cancelMeeting(message)} sx={{ marginBottom: 3, width: 150, height: 32 }}>
          {canceling ? <CabSpinner scale={0.7} sx={{color: colors.white900}} /> : "Cancel Meeting"}
        </CabButton>
      </Box>
    </StyledBox>
  );
};

const SuccessCancelationView = () => {
  return (
    <StyledBox>
      <TitleTypography>Meeting was successfully canceled!</TitleTypography>
    </StyledBox>
  );
};

const CanceledCancelationView = () => {
  return (
    <StyledBox>
      <TitleTypography>This meeting has already been canceled.</TitleTypography>
    </StyledBox>
  );
};

const ErrorCancelationView = ({user}: {user?: CreateUser}) => {
  return (
    <StyledBox sx={{ flexDirection: "column" }}>
      <TitleTypography sx={{marginBottom: 3}}>Sorry, this meeting could not be canceled.</TitleTypography>
      <Typography>Contact {user?.first_name} {user?.last_name} ({user?.email}) to cancel this meeting.</Typography>
    </StyledBox>
  );
};

const StyledBox = styled(Box, {label: "StyledBox"})({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  width: "100%",
});

const TitleTypography = styled(Typography, {label: "TitleTypograpgy"})({
  fontSize: 24,
  fontWeight: "bold",
  color: colors.navyPrimary
});

export default MeetingCancelation;
